/**
 * demo-melodies.ts — built-in demo target melodies for the scoring console.
 *
 * Authored as plain note lists and run through `parseTargetMelody`, so they are
 * guaranteed valid, sorted, and de-duplicated like any external melody. Real song
 * content is injected later; these only exercise the format end-to-end.
 */

import { parseTargetMelody } from './target-melody';

import type { TargetMelody, TargetNote } from './target-melody';

/** Build evenly-spaced notes from a midi sequence at a fixed note length. */
const sequence = (midis: number[], noteMs: number): TargetNote[] =>
  midis.map((midi, i) => ({ midi, startMs: i * noteMs, durMs: noteMs }));

/** C major scale, one octave up (C4→C5), quarter notes at 120 BPM. */
export const DEMO_C_MAJOR_SCALE: TargetMelody = parseTargetMelody({
  id: 'demo-c-major-scale',
  title: 'C Major Scale',
  bpm: 120,
  notes: sequence([60, 62, 64, 65, 67, 69, 71, 72], 500),
});

/** "Twinkle Twinkle Little Star" opening phrase, quarter notes + a final half. */
export const DEMO_TWINKLE: TargetMelody = parseTargetMelody({
  id: 'demo-twinkle',
  title: 'Twinkle Twinkle Little Star',
  bpm: 120,
  notes: [
    { midi: 60, startMs: 0, durMs: 500, lyric: 'Twin' },
    { midi: 60, startMs: 500, durMs: 500, lyric: 'kle' },
    { midi: 67, startMs: 1000, durMs: 500, lyric: 'twin' },
    { midi: 67, startMs: 1500, durMs: 500, lyric: 'kle' },
    { midi: 69, startMs: 2000, durMs: 500, lyric: 'lit' },
    { midi: 69, startMs: 2500, durMs: 500, lyric: 'tle' },
    { midi: 67, startMs: 3000, durMs: 1000, lyric: 'star' },
  ],
});

/**
 * Single sustained A4 (440 Hz), one 7-second note. Easiest possible target:
 * no note changes, no timing — just hold one pitch. For verifying that raw f0
 * scoring works at all before layering melody/timing difficulty on top.
 */
export const DEMO_SUSTAINED_A4: TargetMelody = parseTargetMelody({
  id: 'demo-sustained-a4',
  title: 'Sustained A4 (hold one note)',
  notes: [{ midi: 69, startMs: 0, durMs: 7000, lyric: 'aaah' }],
});

/**
 * Slow C–E–G major triad, each note held ~1.75s. A gentle three-note step up
 * from the single sustained tone: slow enough to hear and match without a guide.
 */
export const DEMO_SLOW_TRIAD: TargetMelody = parseTargetMelody({
  id: 'demo-slow-triad',
  title: 'Slow C–E–G',
  notes: sequence([60, 64, 67], 1750),
});

export const DEMO_MELODIES: TargetMelody[] = [
  DEMO_SUSTAINED_A4,
  DEMO_SLOW_TRIAD,
  DEMO_C_MAJOR_SCALE,
  DEMO_TWINKLE,
];
